import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Built-in fallback in case the backend hasn't responded yet.
const FALLBACK = [
  { name: "roll", label: "Roll", default_unit: "m", builtin: true },
  { name: "sheet", label: "Sheet", default_unit: "sheet", builtin: true },
  { name: "block", label: "Block", default_unit: "block", builtin: true },
  { name: "box", label: "Box / Item", default_unit: "unit", builtin: true },
  { name: "drum", label: "Drum", default_unit: "drum", builtin: true },
  { name: "tub", label: "Tub", default_unit: "tub", builtin: true },
];

// Types that show length/width/thickness inputs in the UI.
const DIMENSIONAL = new Set(["roll", "sheet", "block"]);

const Ctx = createContext({ types: FALLBACK, reload: () => {} });

export const StockTypesProvider = ({ children }) => {
  const [types, setTypes] = useState(FALLBACK);
  const { user } = useAuth();

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/stock-types");
      if (Array.isArray(data) && data.length > 0) setTypes(data);
    } catch (e) {
      console.warn("Stock types load failed:", e);
    }
  }, []);

  useEffect(() => {
    // Stock types endpoint requires auth; only fetch once logged in.
    if (user) reload();
  }, [user, reload]);

  const value = useMemo(
    () => ({
      types,
      names: types.map((t) => t.name),
      labelFor: (name) =>
        types.find((t) => t.name === name)?.label || name,
      unitFor: (name) =>
        types.find((t) => t.name === name)?.default_unit || "unit",
      isDimensional: (name) => DIMENSIONAL.has(name),
      reload,
    }),
    [types, reload]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useStockTypes = () => useContext(Ctx);
